import { Component, computed, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatList, MatListItem } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { KeyValueFormulaFieldModel, KeyValueListType } from '@models/common';
import {
  ComparisonOperation,
  ComparisonOperationKey,
  DateOperation,
  DateOperationKey,
  FormulaOperation,
  FunctionOperation,
  FunctionOperationKey,
  LogicalOperation,
  LogicalOperationKey,
  MathematicalOperation,
  MathematicalOperationKey,
  TextOperation,
  TextOperationKey,
} from '@models/helpers/formula-operation';
import { FormulaOperationHelper } from '@models/helpers/formula-operation-helper';
import { FieldTypeEnum } from '@models/module/fields/enums';
import { FormulaField } from '@module-definition/formulas/cipo-formula/formula.model';
import { FormulaTestDialogComponent } from '@module-definition/formulas/formula-test-dialog/formula-test-dialog.component';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CipoDialogHeaderComponent } from '@shared/dialogs';
import { CipoFormModule } from '@shared/modules';
import { UserService } from '@shared/services/user.service';
import { Observable } from 'rxjs';
import { HighlightRule, TextHighlightDirective } from '../../../../shared/directives/text-highlight.directive';
import { FormulaValidationService } from './formula-validation.service';
import jsonLogic from './json-logic-operators.service';

@Component({
  selector: 'cipo-formula',
  templateUrl: './formula-wizard.component.html',
  styleUrls: ['./formula-wizard.component.scss'],
  imports: [
    CipoFormModule,
    MatCardModule,
    MatChipsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatTooltipModule,
    FlexLayoutModule,
    TranslateModule,
    MatList,
    MatListItem,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatDivider,
    MatAutocompleteModule,
    TextHighlightDirective,
    CipoDialogHeaderComponent,
  ],
})
export class FormulasComponent implements OnInit {
  @ViewChild('formulaInput') formulaInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  readonly dialogRef = inject(MatDialogRef<FormulasComponent>);
  dialogData = inject(MAT_DIALOG_DATA);
  dialog = inject(MatDialog);
  private translate = inject(TranslateService);
  private formulaValidationService = inject(FormulaValidationService);
  userService = inject(UserService);

  fieldOptions = signal<FormulaField[]>([]);
  private formulaValue = signal<string>('');
  private isAutocompleteUpdate = false;
  private lastStableValue = '';
  private afterCursorValue = '';
  isFormulaValid = signal<boolean>(false);

  // Computed highlight rules for formula syntax highlighting
  formulaHighlightRules = computed<HighlightRule[]>(() => {
    const rules: HighlightRule[] = [];

    // Add parentheses highlighting first
    rules.push({
      pattern: /(\(|\))/g,
      className: 'formula-operator',
    });

    // Add field highlighting
    this.fieldOptions().forEach(field => {
      const fieldName = this.getFieldDisplayName(field);
      if (fieldName) {
        rules.push({
          pattern: new RegExp(`(^|[^a-zA-Z0-9_])(${this.escapeRegExp(fieldName)})(?=[^a-zA-Z0-9_]|$)`, 'gi'),
          className: 'formula-field',
          group: 2,
        });
      }
    });

    // Add operator highlighting
    this.combinedOperatorOptions.forEach(op => {
      if (op.value) {
        rules.push({
          pattern: this.escapeRegExp(op.value),
          className: 'formula-operator',
          caseSensitive: false,
        });
      }
    });
    return rules;
  });

  FormulaOperationEnum = FormulaOperation;
  LogicalOperationEnum = LogicalOperation;
  MathematicalOperationEnum = MathematicalOperation;
  ComparisonOperationEnum = ComparisonOperation;
  TextOperationEnum = TextOperation;
  DateOperationEnum = DateOperation;
  FunctionOperationEnum = FunctionOperation;
  FieldTypeEnum = FieldTypeEnum;

  formulaControl = new FormControl('', [Validators.required]);
  formulaError: string | null = null;
  formulaErrorPosition: { start?: number; end?: number } | null = null;
  formulaSuccess: string | null = null;
  availableFormulas: KeyValueFormulaFieldModel<string>[] = [];
  fieldDetailsMap = new Map<number, FormulaField>();

  logicalOperators: LogicalOperationKey[] = Object.keys(LogicalOperation).filter(key =>
    isNaN(Number(key)),
  ) as LogicalOperationKey[];
  mathematicalOperators: MathematicalOperationKey[] = Object.keys(MathematicalOperation).filter(key =>
    isNaN(Number(key)),
  ) as MathematicalOperationKey[];
  comparisonOperators: ComparisonOperationKey[] = Object.keys(ComparisonOperation).filter(key =>
    isNaN(Number(key)),
  ) as ComparisonOperationKey[];
  textOperators: TextOperationKey[] = Object.keys(TextOperation).filter(key =>
    isNaN(Number(key)),
  ) as TextOperationKey[];
  dateOperators: DateOperationKey[] = Object.keys(DateOperation).filter(key =>
    isNaN(Number(key)),
  ) as DateOperationKey[];
  functionOperators: FunctionOperationKey[] = Object.keys(FunctionOperation).filter(key =>
    isNaN(Number(key)),
  ) as FunctionOperationKey[];

  formattedLogicalOperators = this.logicalOperators.map(op => ({
    key: LogicalOperation[op as LogicalOperationKey],
    value: op as LogicalOperationKey,
  }));
  formattedMathematicalOperators = this.mathematicalOperators.map(op => ({
    key: MathematicalOperation[op as MathematicalOperationKey],
    value: op as MathematicalOperationKey,
  }));
  formattedComparisonOperators = this.comparisonOperators.map(op => ({
    key: ComparisonOperation[op as ComparisonOperationKey],
    value: op as ComparisonOperationKey,
  }));
  formattedTextOperators = this.textOperators.map(op => ({
    key: TextOperation[op as TextOperationKey],
    value: op as TextOperationKey,
  }));
  formattedDateOperators = this.dateOperators.map(op => ({
    key: DateOperation[op as DateOperationKey],
    value: op as DateOperationKey,
  }));
  formattedFunctionOperators = this.functionOperators.map(op => ({
    key: FunctionOperation[op as FunctionOperationKey],
    value: op as FunctionOperationKey,
  }));

  cursorPosition: number = 0;
  combinedOperatorOptions: KeyValueListType[] = [];

  operatorSearchControl = new FormControl('');
  fieldSearchControl = new FormControl('');
  filteredFieldOptions: Observable<(FormulaField | KeyValueListType)[]>;

  operatorGroups = [
    { category: 'formulas.categories.logical', items: this.formattedLogicalOperators },
    { category: 'formulas.categories.mathematical', items: this.formattedMathematicalOperators },
    { category: 'formulas.categories.comparison', items: this.formattedComparisonOperators },
    { category: 'formulas.categories.text', items: this.formattedTextOperators },
    { category: 'formulas.categories.date', items: this.formattedDateOperators },
    { category: 'formulas.categories.function', items: this.formattedFunctionOperators },
  ];

  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  constructor() {
    this.fieldOptions.set([]);
    this.setupCombinedOperators();

    this.formulaControl.valueChanges.subscribe(value => {
      if (!this.isAutocompleteUpdate) {
        if (typeof value === 'string') {
          this.formulaValue.set(value || '');
          this.autoValidateFormula(value || '');
        }
      }
    });
  }

  private setupCombinedOperators() {
    this.combinedOperatorOptions = [];

    this.formattedLogicalOperators.forEach(op => this.combinedOperatorOptions.push({ key: op.value, value: op.value }));

    this.formattedMathematicalOperators.forEach(op =>
      this.combinedOperatorOptions.push({ key: op.value, value: op.value }),
    );

    this.formattedComparisonOperators.forEach(op =>
      this.combinedOperatorOptions.push({ key: op.value, value: op.value }),
    );

    this.formattedTextOperators.forEach(op => this.combinedOperatorOptions.push({ key: op.value, value: op.value }));

    this.formattedDateOperators.forEach(op => this.combinedOperatorOptions.push({ key: op.value, value: op.value }));

    this.formattedFunctionOperators.forEach(op =>
      this.combinedOperatorOptions.push({ key: op.value, value: op.value }),
    );
  }

  ngOnInit() {
    if (this.dialogData?.fields) {
      this.fieldOptions.set(this.dialogData.fields as FormulaField[]);
      this.fieldDetailsMap = new Map((this.dialogData.fields as FormulaField[]).map(f => [f.id, f]));
    }
    setTimeout(() => this.autocompleteTrigger?.closePanel(), 0);
  }

  insertIntoFormula(option: FormulaField | KeyValueListType<string>) {
    this.isAutocompleteUpdate = true;
    this.updateCursorContext();

    let textToInsert = '';

    if ((option as FormulaField).label) {
      textToInsert = (option as FormulaField).label;
    } else if ((option as KeyValueListType<string>).value) {
      textToInsert = (option as KeyValueListType<string>).value;
    } else {
      textToInsert = option.toString();
    }

    const beforePart = this.lastStableValue || '';
    const afterPart = this.afterCursorValue || '';
    const newValue = beforePart + textToInsert + afterPart;

    this.formulaControl.setValue(newValue, { emitEvent: false });
    this.formulaValue.set(newValue);

    if (this.formulaInput?.nativeElement) {
      this.formulaInput.nativeElement.value = newValue;
      this.dispatchInputEvent(this.formulaInput.nativeElement);
    }

    this.autoValidateFormula(newValue);

    requestAnimationFrame(() => {
      this.isAutocompleteUpdate = false;

      if (this.formulaInput?.nativeElement) {
        const newCursorPos = beforePart.length + textToInsert.length;
        this.cursorPosition = newCursorPos;
        this.formulaInput.nativeElement.setSelectionRange(newCursorPos, newCursorPos);

        this.updateCursorContextAfterInsertion(newCursorPos);
      }
    });
  }

  private runFormulaValidation(
    formula: string,
    withMessages: boolean = false,
  ): { isValid: boolean; validationResult?: any } {
    this.isFormulaValid.set(false);

    if (!formula.trim() || this.fieldOptions().length === 0) {
      if (withMessages) {
        this.formulaError = null;
        this.formulaErrorPosition = null;
        this.formulaSuccess = null;
      }
      return { isValid: false };
    }

    try {
      const validationResult = this.formulaValidationService.validateFormula(
        formula,
        this.fieldOptions(),
        //this.fieldDetailsMap,
      );

      if (!validationResult.isValid) {
        if (withMessages) {
          const error = validationResult.errors[0];
          this.formulaError = error.message;
          if (error.startPosition !== undefined && error.endPosition !== undefined) {
            this.formulaErrorPosition = {
              start: error.startPosition,
              end: error.endPosition,
            };
            requestAnimationFrame(() => this.highlightErrorPosition());
          }
          this.formulaSuccess = null;
        }
        return { isValid: false, validationResult };
      }

      if (validationResult.jsonLogicObj) {
        jsonLogic.apply(validationResult.jsonLogicObj, {});
      }

      if (withMessages) {
        this.formulaSuccess = this.translate.instant('formulas.success.formulaValid');
        this.formulaError = null;
        this.formulaErrorPosition = null;
      }
      this.isFormulaValid.set(true);
      return { isValid: true, validationResult };
    } catch (e: any) {
      if (withMessages) {
        this.formulaError = this.translate.instant('formulas.errors.formulaExecutionError', { error: e.message || e });
        this.formulaErrorPosition = null;
        this.formulaSuccess = null;
      }
      this.isFormulaValid.set(false);
      return { isValid: false };
    }
  }

  validate() {
    this.updateCursorPosition();
    this.runFormulaValidation(this.formulaControl.value || '', true);
  }

  private autoValidateFormula(formula: string) {
    this.runFormulaValidation(formula, false);
  }

  testFormula() {
    this.formulaSuccess = null;
    this.updateCursorPosition();

    const { isValid, validationResult } = this.runFormulaValidation(this.formulaControl.value || '', true);

    if (!isValid) {
      return;
    }

    this.dialog
      .open(FormulaTestDialogComponent, {
        panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
        data: {
          formula: this.formulaControl.value,
          usedFieldIds: validationResult.usedFieldIds || [],
          fieldOptions: this.fieldOptions(),
          fieldDetailsMap: this.fieldDetailsMap,
        },
        ...this.userService.getResponsiveDialogSize().md,
      })
      .afterClosed()
      .subscribe(result => {});
  }

  getOperatorTooltip(operatorKey: string): string {
    const operationDefinition = FormulaOperationHelper.getOperationDefinition(operatorKey as any);
    if (operationDefinition) {
      const parts = [`${operationDefinition.name}`, '', `Description: ${operationDefinition.description}`];

      if (operationDefinition.syntax) {
        parts.push('', `Syntax: ${operationDefinition.syntax}`);
      }

      if (operationDefinition.example) {
        parts.push('', `Example: ${operationDefinition.example}`);
      }

      return parts.join('\n');
    }
    return 'Click for operator details';
  }

  useFormula() {
    if (this.isFormulaValid()) {
      this.dialogRef.close(this.formulaControl.value);
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  getFieldDisplayName(field: FormulaField | KeyValueListType<string>): string {
    if ((field as FormulaField).label) {
      return (field as FormulaField).label;
    }
    return (field as KeyValueListType<string>).value;
  }

  clearErrorHighlight(): void {
    this.formulaError = null;
    this.formulaErrorPosition = null;
    this.formulaSuccess = null;
    this.updateCursorPosition();
  }

  public onFormulaInputEvent(event: Event): void {
    this.updateCursorContextForAutocomplete();

    if (event.type === 'keyup') {
      const keyEvent = event as KeyboardEvent;
      if (keyEvent.code === 'Space') {
        requestAnimationFrame(() => {
          if (this.formulaInput?.nativeElement) {
            this.dispatchInputEvent(this.formulaInput.nativeElement);
          }
        });
      }
    }
  }

  public updateCursorPosition(): void {
    const inputEl = this.formulaInput?.nativeElement;
    if (inputEl) {
      this.cursorPosition = inputEl.selectionStart ?? 0;
    }
  }

  private updateCursorContext(): void {
    const inputEl = this.formulaInput?.nativeElement;
    if (inputEl) {
      this.cursorPosition = inputEl.selectionStart ?? 0;

      const currentValue = inputEl.value || '';
      const cursorPos = inputEl.selectionStart || 0;

      this.lastStableValue = currentValue.substring(0, cursorPos);
      this.afterCursorValue = currentValue.substring(cursorPos);
    }
  }

  private updateCursorContextForAutocomplete(): void {
    const inputEl = this.formulaInput?.nativeElement;
    if (inputEl) {
      this.cursorPosition = inputEl.selectionStart ?? 0;

      const currentValue = inputEl.value || '';
      const cursorPos = inputEl.selectionStart || 0;

      const beforeCursor = currentValue.substring(0, cursorPos);
      const wordMatch = beforeCursor.match(/[a-zA-Z]*$/);
      const currentWord = wordMatch ? wordMatch[0] : '';

      const wordStart = currentWord.length > 0 ? beforeCursor.length - currentWord.length : cursorPos;

      this.lastStableValue = currentValue.substring(0, wordStart);
      this.afterCursorValue = currentValue.substring(cursorPos);
    }
  }

  private updateCursorContextAfterInsertion(newCursorPos: number): void {
    const inputEl = this.formulaInput?.nativeElement;
    if (inputEl) {
      const currentValue = inputEl.value || '';

      this.lastStableValue = currentValue.substring(0, newCursorPos);
      this.afterCursorValue = currentValue.substring(newCursorPos);
    }
  }

  get displayCursorPosition(): number {
    if (!this.formulaControl.value || this.formulaControl.value.length === 0) {
      return 0;
    }
    if (this.cursorPosition == null) {
      return this.formulaControl.value.length;
    }
    return this.cursorPosition;
  }

  highlightErrorPosition(): void {
    if (this.formulaErrorPosition && this.formulaInput) {
      const inputElement = this.formulaInput.nativeElement;
      inputElement.focus();
      inputElement.setSelectionRange(this.formulaErrorPosition.start!, this.formulaErrorPosition.end!);
      setTimeout(() => this.autocompleteTrigger?.closePanel(), 0);
    }
  }

  getOperationDefinition(op: { key: any; value: string }) {
    return (
      FormulaOperationHelper.getOperationDefinition(op.value as any) ||
      FormulaOperationHelper.getOperationDefinition(op.key as any)
    );
  }

  filteredFields() {
    const search = this.fieldSearchControl.value?.toLowerCase() || '';
    return this.fieldOptions().filter(f => f.label.toLowerCase().includes(search));
  }

  filteredOperatorGroups() {
    const search = this.operatorSearchControl.value?.toLowerCase() || '';
    return this.operatorGroups
      .map(group => ({
        ...group,
        items: group.items.filter(op => op.value.toLowerCase().includes(search)),
      }))
      .filter(group => group.items.length > 0);
  }

  getFilteredFields(): FormulaField[] {
    const search = this.fieldSearchControl.value?.toLowerCase() || '';
    return this.fieldOptions().filter(f => f.label.toLowerCase().includes(search));
  }

  getFilteredOperators(): KeyValueListType<string>[] {
    const search = this.operatorSearchControl.value?.toLowerCase() || '';
    return this.combinedOperatorOptions.filter(op => op.value.toLowerCase().includes(search));
  }

  onFieldOptionSelected(event: any) {
    event.preventDefault?.();

    const selectedValue = event.option.value;
    let valueToInsert = '';

    if ((selectedValue as FormulaField).label) {
      valueToInsert = (selectedValue as FormulaField).label;
    } else if ((selectedValue as KeyValueListType<string>).value) {
      valueToInsert = (selectedValue as KeyValueListType<string>).value;
    } else {
      valueToInsert = selectedValue.toString();
    }

    const beforePart = this.lastStableValue || '';
    const afterPart = this.afterCursorValue || '';
    const newValue = beforePart + valueToInsert + afterPart;

    this.formulaControl.setValue(newValue, { emitEvent: false });
    this.formulaValue.set(newValue);

    if (this.formulaInput?.nativeElement) {
      this.formulaInput.nativeElement.value = newValue;
      this.dispatchInputEvent(this.formulaInput.nativeElement);
    }

    this.autoValidateFormula(newValue);

    requestAnimationFrame(() => {
      if (this.formulaInput?.nativeElement) {
        const newCursorPos = beforePart.length + valueToInsert.length;
        this.cursorPosition = newCursorPos;
        this.formulaInput.nativeElement.setSelectionRange(newCursorPos, newCursorPos);

        this.updateCursorContextAfterInsertion(newCursorPos);
      }
    });
  }

  displayFn(option: any): string {
    if (!option) return '';
    if (typeof option === 'string') return option;
    if (option.formula && option.formula.name) return option.formula.name;
    if (option.formula && option.formula.value) {
      const match = option.formula.value.match(/\] (.+)$/);
      return match ? match[1] : option.formula.value;
    }
    return option.value || '';
  }

  clearFormula(): void {
    this.formulaControl.setValue('');
    this.formulaValue.set('');
    this.isFormulaValid.set(false);
    this.clearErrorHighlight();

    if (this.formulaInput?.nativeElement) {
      this.formulaInput.nativeElement.value = '';
      this.dispatchInputEvent(this.formulaInput.nativeElement);
    }
  }

  insertOperatorAndStopPropagation(operator: string, event: Event): void {
    event.stopPropagation();
    this.insertIntoFormula({ key: operator, value: operator });
  }

  // Dispatch a native 'input' event so Angular and listeners detect programmatic value changes
  private dispatchInputEvent(element: HTMLInputElement) {
    const inputEvent = new Event('input', { bubbles: true });
    element.dispatchEvent(inputEvent);
  }
}
